import { Component } from "@angular/core";
import { ICellRendererAngularComp } from "ag-grid-angular";
import { ICellRendererParams } from "ag-grid-community";
import { DetailRow, RD_STATUS, RecurringRow } from "./recurring-detector.service";
import { formatAbsAmount } from "./transaction.utils";

interface RdDescriptionParams extends ICellRendererParams<RecurringRow, string> {
  onDetail: (row: RecurringRow) => void;
}

@Component({
  selector: "app-rd-description-renderer",
  template: `<span class="rd-detail-link" (click)="onClick()">{{ value }}</span>`,
})
export class RdDescriptionRendererComponent implements ICellRendererAngularComp {
  value = "";
  private row: RecurringRow | undefined;
  private onDetail!: (row: RecurringRow) => void;

  agInit(params: RdDescriptionParams): void {
    this.value = params.value ?? "";
    this.row = params.data;
    this.onDetail = params.onDetail;
  }

  refresh(params: RdDescriptionParams): boolean {
    this.value = params.value ?? "";
    this.row = params.data;
    return true;
  }

  onClick(): void {
    if (this.row) {
      this.onDetail(this.row);
    }
  }
}

@Component({
  selector: "app-rd-avg-amount-renderer",
  template: `{{ text }}
    @if (hasVariable) {
      <span class="rd-amount-variable" title="Amount varies">~</span>
    }`,
})
export class RdAvgAmountRendererComponent implements ICellRendererAngularComp {
  text = "";
  hasVariable = false;

  agInit(params: ICellRendererParams<RecurringRow, number>): void {
    this.setData(params);
  }

  refresh(params: ICellRendererParams<RecurringRow, number>): boolean {
    this.setData(params);
    return true;
  }

  private setData(params: ICellRendererParams<RecurringRow, number>): void {
    if (!params.data) {
      this.text = "";
      this.hasVariable = false;
      return;
    }
    const amt = formatAbsAmount(params.data.avgAmount);
    this.text = params.data.symbol ? `${params.data.symbol} ${amt}` : amt;
    this.hasVariable = params.data.hasVariableAmount;
  }
}

@Component({
  selector: "app-rd-next-due-renderer",
  template: `<span [class]="cls">{{ text }}</span>`,
})
export class RdNextDueRendererComponent implements ICellRendererAngularComp {
  text = "";
  cls = "";

  agInit(params: ICellRendererParams<RecurringRow, Date | null>): void {
    this.setData(params);
  }

  refresh(params: ICellRendererParams<RecurringRow, Date | null>): boolean {
    this.setData(params);
    return true;
  }

  private setData(params: ICellRendererParams<RecurringRow, Date | null>): void {
    if (!params.value) {
      this.cls = "rd-date-none";
      this.text = "—";
      return;
    }
    const todayUtcMidnight = new Date();
    todayUtcMidnight.setUTCHours(0, 0, 0, 0);
    const d = params.value;
    const dateStr = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
    const isOverdue = d < todayUtcMidnight;
    this.cls = isOverdue ? "rd-date-overdue" : "rd-date-normal";
    this.text = isOverdue ? `${dateStr} ⚠` : dateStr;
  }
}

@Component({
  selector: "app-rd-status-renderer",
  template: `<span [class]="cls">{{ value }}</span>`,
})
export class RdStatusRendererComponent implements ICellRendererAngularComp {
  value = "";
  cls = "";

  agInit(params: ICellRendererParams<RecurringRow, string>): void {
    this.setData(params);
  }

  refresh(params: ICellRendererParams<RecurringRow, string>): boolean {
    this.setData(params);
    return true;
  }

  private setData(params: ICellRendererParams<RecurringRow, string>): void {
    this.value = params.value ?? "";
    this.cls = params.value === RD_STATUS.CONFIRMED ? "rd-status-confirmed" : "rd-status-other";
  }
}

interface RdActionParams extends ICellRendererParams<RecurringRow> {
  onMove: (row: RecurringRow) => void;
  isRecurring: boolean;
}

@Component({
  selector: "app-rd-action-renderer",
  template: `<button class="rd-move-btn" [disabled]="!row" (click)="onClick()">
    {{ label }}
  </button>`,
})
export class RdActionRendererComponent implements ICellRendererAngularComp {
  label = "";
  row: RecurringRow | undefined;
  private onMove!: (row: RecurringRow) => void;

  agInit(params: RdActionParams): void {
    this.label = params.isRecurring ? "Move to Other" : "Mark as Recurring";
    this.row = params.data;
    this.onMove = params.onMove;
  }

  refresh(params: RdActionParams): boolean {
    this.row = params.data;
    return true;
  }

  onClick(): void {
    if (this.row) {
      this.onMove(this.row);
    }
  }
}

@Component({
  selector: "app-rd-change-renderer",
  template: `<span [class]="cls">{{ text }}</span>`,
})
export class RdChangeRendererComponent implements ICellRendererAngularComp {
  text = "";
  cls = "";

  agInit(params: ICellRendererParams<DetailRow, number | null>): void {
    this.setData(params);
  }

  refresh(params: ICellRendererParams<DetailRow, number | null>): boolean {
    this.setData(params);
    return true;
  }

  private setData(params: ICellRendererParams<DetailRow, number | null>): void {
    if (params.value == null || params.value === 0) {
      this.cls = "rd-change-none";
      this.text = "—";
      return;
    }
    const sign = params.value > 0 ? "+" : "-";
    this.cls = params.value > 0 ? "rd-change-increase" : "rd-change-decrease";
    this.text = `${sign}${formatAbsAmount(params.value)}`;
  }
}
